import { FC } from 'react'

import { SettingsPageLayout } from '../../components/SettingsPageLayout'
import {
  InputSelectControl,
  SettingsFormControl,
  SettingsGroup,
  Switch,
  TextInputField
} from '@sequencehq/core-components'

import { useMutation, useQuery } from '@sequencehq/api'

import { BASE_INPUT_FIELD_STYLES } from '../../settings.constants'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useForm } from '@sequencehq/utils'
import {
  greaterThanEqualTo,
  isNumber,
  required,
  requiredAllowZero
} from '@sequencehq/utils/validation'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { PaymentTermsSelect } from 'components/PaymentTerms/PaymentTermsSelect'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { AccountingLockDateSettings } from '../../components/merchant/AccountingLockDateSettings'
import { PaymentProviderSettings } from './PaymentProviderSettings'

type AccountingDatePreference =
  | 'END_OF_BILLING_PERIOD'
  | 'INTENDED_INVOICE_CREATION_DATE'
  | 'INVOICE_ISSUE_DATE'

type InvoiceDueDateCalculatedFrom = 'ACCOUNTING_DATE' | 'ISSUE_DATE'

export const InvoicesSettingsPage: FC = () => {
  const { displayNotification } = useNotifications()
  const { showDueDateBehaviourSetting } = useFeatureFlags()

  const merchantSettings = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )

  const merchantSettingsMutator = useMutation(
    dashboard20240730Client.putMerchant,
    {
      onSuccess() {
        displayNotification('Invoice settings updated', {
          type: 'success'
        })
      },
      onError() {
        displayNotification('Invoice settings failed to updated', {
          type: 'error'
        })
      }
    }
  )

  const invoiceAccountingDate = useQuery(
    dashboardv20240509Client.getInvoiceAccountingDateSettings
  )

  const invoiceAccountingDateMutator = useMutation(
    dashboardv20240509Client.putInvoiceAccountingDateSettings,
    {
      onSuccess() {
        displayNotification('Invoice settings updated', {
          type: 'success'
        })
      },
      onError() {
        displayNotification('Invoice settings failed to updated', {
          type: 'error'
        })
      }
    }
  )

  const { fields, queries } = useForm({
    value: {
      invoiceNumberPrefix: merchantSettings.data?.invoiceNumberPrefix,
      lastInvoiceNumber: merchantSettings.data?.lastInvoiceNumber,
      invoiceAccountingDate: invoiceAccountingDate.data?.preference,
      creditNoteNumberPrefix: merchantSettings.data?.creditNoteNumberPrefix,
      lastCreditNoteNumber: merchantSettings.data?.lastCreditNoteNumber
    },
    showValidationErrors: true,
    fieldConfiguration: [
      {
        property: 'invoiceNumberPrefix',
        validation: [required]
      },
      {
        property: 'lastInvoiceNumber',
        validation: [
          isNumber,
          requiredAllowZero,
          ...(typeof merchantSettings.data?.lastInvoiceNumber === 'number'
            ? [greaterThanEqualTo(merchantSettings.data?.lastInvoiceNumber)]
            : [])
        ]
      },
      {
        property: 'creditNoteNumberPrefix',
        validation: [required]
      },
      {
        property: 'lastCreditNoteNumber',
        validation: [
          isNumber,
          requiredAllowZero,
          ...(typeof merchantSettings.data?.lastCreditNoteNumber === 'number'
            ? [greaterThanEqualTo(merchantSettings.data?.lastCreditNoteNumber)]
            : [])
        ]
      }
    ]
  })

  const accountPaymentSettings = useQuery(
    dashboard20240730Client.getAccountPaymentSettings
  )

  const accountPaymentSettingsMutator = useMutation(
    dashboard20240730Client.putAccountPaymentSettings,
    {
      onSuccess() {
        displayNotification('Payment settings updated', {
          type: 'success'
        })
      },
      onError() {
        displayNotification('Payment settings failed to update', {
          type: 'error'
        })
      }
    }
  )

  const invoiceRenderSettings = useQuery(
    dashboard20240730Client.getInvoicesRenderSettings
  )

  const mutateInvoiceRenderSettings = useMutation(
    dashboard20240730Client.putInvoicesRenderSettings,
    {
      onSuccess() {
        displayNotification('Payment settings updated', {
          type: 'success'
        })
      },
      onError() {
        displayNotification('Payment settings failed to update', {
          type: 'error'
        })
      }
    }
  )

  const creditNotesSettings = useQuery(
    dashboard20240730Client.getCreditNotesSettings
  )

  const creditNotesMutator = useMutation(
    dashboard20240730Client.putCreditNotesSettings,
    {
      onSuccess() {
        displayNotification('Credit notes updated', {
          type: 'success'
        })
      },
      onError() {
        displayNotification('Credit notes failed to update', {
          type: 'error'
        })
      }
    }
  )

  const stripeCollectionSettings = useQuery(
    dashboardv99990101Client.getStripeCollectionsSettings
  )

  const stripeCollectionSettingsMutator = useMutation(
    dashboardv99990101Client.putStripeCollectionsSettings,
    {
      onSuccess() {
        displayNotification('Payment settings updated', {
          type: 'success'
        })
      },
      onError() {
        displayNotification('Payment settings failed to update', {
          type: 'error'
        })
      }
    }
  )

  const billingSettings = useQuery(dashboard20240730Client.getBillingSettings)

  const billingSettingsMutator = useMutation(
    dashboard20240730Client.putBillingSettings,
    {
      onSuccess() {
        displayNotification('Billing schedule settings updated', {
          type: 'success'
        })
      },
      onError() {
        displayNotification('Billing schedule settings failed to update', {
          type: 'error'
        })
      }
    }
  )

  const isError =
    merchantSettings.isError ||
    invoiceAccountingDate.isError ||
    accountPaymentSettings.isError ||
    invoiceRenderSettings.isError ||
    creditNotesSettings.isError ||
    stripeCollectionSettings.isError ||
    billingSettings.isError

  return (
    <SettingsPageLayout header="Invoices" isError={isError}>
      <SettingsGroup
        isLoading={
          invoiceAccountingDate.isLoading ||
          invoiceAccountingDate.isLoading ||
          merchantSettings.isLoading
        }
        rows={5}
      >
        <SettingsFormControl
          label="Default payment terms"
          htmlFor="default-payment-terms"
        >
          <PaymentTermsSelect
            id="default-payment-terms"
            value={merchantSettings.data?.defaultDueDateDays}
            onChange={value => {
              if (!merchantSettings.data) return
              merchantSettingsMutator.mutate({
                id: merchantSettings.data.id,
                body: {
                  legalCompanyName: merchantSettings.data.legalCompanyName,
                  address: merchantSettings.data.address,
                  email: merchantSettings.data.email,
                  bankAccountDetails: merchantSettings.data.bankAccountDetails,
                  defaultDueDateDays: value
                }
              })
            }}
            isPending={merchantSettingsMutator.isPending}
            triggerStyle={{
              width: BASE_INPUT_FIELD_STYLES.width
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl
          label="Invoice date behaviour"
          helper="Determines the accounting date set on the invoice."
          htmlFor="invoce-date-behaviour"
        >
          <InputSelectControl
            id="invoce-date-behaviour"
            data-testid="settings.invoices.invoiceDateBehaviour"
            placeholder="Select date behaviour"
            triggerStyle={{
              width: BASE_INPUT_FIELD_STYLES.width
            }}
            initialValue={invoiceAccountingDate.data?.preference}
            options={[
              {
                value: 'END_OF_BILLING_PERIOD',
                label: 'End of billing period (for in-arrears)'
              },
              {
                label: 'Billing date',
                value: 'INTENDED_INVOICE_CREATION_DATE'
              },
              {
                value: 'INVOICE_ISSUE_DATE',
                label: 'Invoice issue date'
              }
            ]}
            onChange={value => {
              if (!invoiceAccountingDate.data) return
              invoiceAccountingDateMutator.mutate({
                id: invoiceAccountingDate.data.id,
                body: {
                  preference: value as AccountingDatePreference
                }
              })
            }}
          />
        </SettingsFormControl>

        {showDueDateBehaviourSetting && (
          <SettingsFormControl
            label="Due date behaviour"
            // Helper copy truncates past ~49 characters (fixed-width row, nowrap).
            helper="Anchors the due date to accounting or issue date."
            htmlFor="due-date-behaviour"
          >
            <InputSelectControl
              id="due-date-behaviour"
              data-testid="settings.invoices.dueDateBehaviour"
              placeholder="Select date behaviour"
              triggerStyle={{
                width: BASE_INPUT_FIELD_STYLES.width
              }}
              initialValue={
                invoiceAccountingDate.data?.invoiceDueDateCalculatedFrom
              }
              options={[
                {
                  value: 'ACCOUNTING_DATE',
                  label: 'Accounting date'
                },
                {
                  value: 'ISSUE_DATE',
                  label: 'Invoice issue date'
                }
              ]}
              onChange={value => {
                if (!invoiceAccountingDate.data) return
                invoiceAccountingDateMutator.mutate({
                  id: invoiceAccountingDate.data.id,
                  body: {
                    invoiceDueDateCalculatedFrom:
                      value as InvoiceDueDateCalculatedFrom
                  }
                })
              }}
            />
          </SettingsFormControl>
        )}
        <SettingsFormControl label="Invoice prefix" htmlFor="invoce-prefix">
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder="INV"
            id="invoce-prefix"
            data-testid="settings.invoices.invoicePrefix"
            value={fields.invoiceNumberPrefix.value}
            validationErrors={fields.invoiceNumberPrefix.validationErrors}
            disabled={merchantSettingsMutator.isPending}
            onChange={e => {
              fields.invoiceNumberPrefix.onChange(e.target.value)
            }}
            onBlur={() => {
              if (
                !merchantSettings.data ||
                !queries.isDirty ||
                fields.invoiceNumberPrefix.validationErrors.length > 0
              ) {
                return
              }

              merchantSettingsMutator.mutate({
                id: merchantSettings.data.id,
                body: {
                  legalCompanyName: merchantSettings.data.legalCompanyName,
                  address: merchantSettings.data.address,
                  email: merchantSettings.data.email,
                  bankAccountDetails: merchantSettings.data.bankAccountDetails,
                  invoiceNumberPrefix: queries.formData.invoiceNumberPrefix
                }
              })
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl
          label="Latest invoice number"
          helper="Finalizing an invoice increments this number."
          htmlFor="latest-invoice-number"
        >
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder="0001"
            id="latest-invoice-number"
            value={fields.lastInvoiceNumber.value}
            validationErrors={fields.lastInvoiceNumber.validationErrors}
            type="number"
            onChange={e => {
              fields.lastInvoiceNumber.onChange(parseInt(e.target.value))
            }}
            disabled={merchantSettingsMutator.isPending}
            onBlur={() => {
              if (
                !merchantSettings.data ||
                !queries.isDirty ||
                fields.lastInvoiceNumber.validationErrors.length > 0
              ) {
                return
              }

              merchantSettingsMutator.mutate({
                id: merchantSettings.data.id,
                body: {
                  legalCompanyName: merchantSettings.data.legalCompanyName,
                  address: merchantSettings.data.address,
                  email: merchantSettings.data.email,
                  bankAccountDetails: merchantSettings.data.bankAccountDetails,
                  lastInvoiceNumber: queries.formData.lastInvoiceNumber
                }
              })
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl
          label="CC your company email address on invoice emails "
          htmlFor="cc-your-company"
        >
          <Switch
            size="md"
            id="cc-your-company"
            as="div"
            isChecked={merchantSettings.data?.ccMerchantOnInvoiceEmails}
            disabled={merchantSettingsMutator.isPending}
            onChange={value => {
              if (!merchantSettings.data) return
              merchantSettingsMutator.mutate({
                id: merchantSettings.data.id,
                body: {
                  legalCompanyName: merchantSettings.data.legalCompanyName,
                  address: merchantSettings.data.address,
                  email: merchantSettings.data.email,
                  bankAccountDetails: merchantSettings.data.bankAccountDetails,
                  ccMerchantOnInvoiceEmails: value
                }
              })
            }}
          />
        </SettingsFormControl>
      </SettingsGroup>
      <SettingsGroup
        title="Billing Schedules"
        footer="Defaults for the schedule editor. Auto-sending can be overridden on an individual schedule, and a phase can be set to reset its billing period on its own."
        isLoading={billingSettings.isLoading}
        rows={3}
      >
        <SettingsFormControl
          label="Enable auto-sending by default for new schedules"
          htmlFor="auto-issue-invoices"
          helper="Invoices are finalized and sent for you."
        >
          <Switch
            size="md"
            id="auto-issue-invoices"
            isChecked={billingSettings.data?.autoIssueInvoices === 'ALL'}
            disabled={billingSettingsMutator.isPending}
            onChange={value => {
              billingSettingsMutator.mutate({
                body: {
                  autoIssueInvoices: value ? 'ALL' : 'NONE'
                }
              })
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl
          label="Apply pricing updates to following phases"
          helper="Updating prices, discount, and minimums will also update following phases"
          htmlFor="phase-editing-mode"
        >
          <Switch
            size="md"
            id="phase-editing-mode"
            isChecked={
              billingSettings.data?.phaseEditingMode ===
              'CASCADE_TO_FUTURE_PHASES'
            }
            disabled={billingSettingsMutator.isPending}
            onChange={value => {
              billingSettingsMutator.mutate({
                body: {
                  phaseEditingMode: value
                    ? 'CASCADE_TO_FUTURE_PHASES'
                    : 'SINGLE'
                }
              })
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl
          label="Reset billing periods for new phases"
          htmlFor="default-reset-billing-period"
        >
          <Switch
            size="md"
            id="default-reset-billing-period"
            isChecked={
              billingSettings.data?.initialPhaseResetBillingPeriod === 'RESET'
            }
            disabled={billingSettingsMutator.isPending}
            onChange={value => {
              billingSettingsMutator.mutate({
                body: {
                  initialPhaseResetBillingPeriod: value
                    ? 'RESET'
                    : 'CONTINUE_FROM_PREVIOUS_PHASE'
                }
              })
            }}
          />
        </SettingsFormControl>
      </SettingsGroup>
      <PaymentProviderSettings
        stripeCollectionSettings={stripeCollectionSettings}
        accountPaymentSettings={accountPaymentSettings}
        invoiceRenderSettings={invoiceRenderSettings}
        stripeCollectionSettingsMutator={stripeCollectionSettingsMutator}
        accountPaymentSettingsMutator={accountPaymentSettingsMutator}
        mutateInvoiceRenderSettings={mutateInvoiceRenderSettings}
      />
      <SettingsGroup
        title="Credit notes"
        footer="Automatically create a cash credit grant for a customer whenever a credit note is issued."
        isLoading={creditNotesSettings.isLoading}
      >
        <SettingsFormControl
          label="Credit note prefix"
          htmlFor="credit-note-prefix"
        >
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder="CN"
            id="credit-note-prefix"
            value={fields.creditNoteNumberPrefix.value}
            validationErrors={fields.creditNoteNumberPrefix.validationErrors}
            disabled={merchantSettingsMutator.isPending}
            onChange={e => {
              fields.creditNoteNumberPrefix.onChange(e.target.value)
            }}
            onBlur={() => {
              if (
                !merchantSettings.data ||
                !queries.isDirty ||
                fields.creditNoteNumberPrefix.validationErrors.length > 0
              ) {
                return
              }

              merchantSettingsMutator.mutate({
                id: merchantSettings.data.id,
                body: {
                  legalCompanyName: merchantSettings.data.legalCompanyName,
                  address: merchantSettings.data.address,
                  email: merchantSettings.data.email,
                  bankAccountDetails: merchantSettings.data.bankAccountDetails,
                  creditNoteNumberPrefix:
                    queries.formData.creditNoteNumberPrefix
                }
              })
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl
          label="Latest credit note number"
          helper="Finalizing an credit note increments this number."
          htmlFor="latest-credit-note-number"
        >
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder="0001"
            id="latest-credit-note-number"
            value={fields.lastCreditNoteNumber.value}
            validationErrors={fields.lastCreditNoteNumber.validationErrors}
            type="number"
            onChange={e => {
              fields.lastCreditNoteNumber.onChange(parseInt(e.target.value))
            }}
            disabled={merchantSettingsMutator.isPending}
            onBlur={() => {
              if (
                !merchantSettings.data ||
                !queries.isDirty ||
                fields.lastCreditNoteNumber.validationErrors.length > 0
              ) {
                return
              }

              merchantSettingsMutator.mutate({
                id: merchantSettings.data.id,
                body: {
                  legalCompanyName: merchantSettings.data.legalCompanyName,
                  address: merchantSettings.data.address,
                  email: merchantSettings.data.email,
                  bankAccountDetails: merchantSettings.data.bankAccountDetails,
                  lastCreditNoteNumber: queries.formData.lastCreditNoteNumber
                }
              })
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl
          label="Create cash credit grants"
          htmlFor="cash-credit-grants"
        >
          <Switch
            size="md"
            as="div"
            id="cash-credit-grants"
            disabled={creditNotesMutator.isPending}
            isChecked={
              creditNotesSettings.data?.generateCashCreditGrant === 'ENABLED'
            }
            onChange={value => {
              creditNotesMutator.mutate({
                body: {
                  generateCashCreditGrant: value ? 'ENABLED' : 'DISABLED'
                }
              })
            }}
          />
        </SettingsFormControl>
      </SettingsGroup>

      <AccountingLockDateSettings />
    </SettingsPageLayout>
  )
}
